import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Document, Page } from "react-pdf";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  ChevronLeft,
  ChevronRight,
  Download,
  FitScreen,
  ZoomIn,
  ZoomOut,
} from "@mui/icons-material";
import { configurePdfWorker } from "../utils/configure-pdf-worker";

const isTestEnvironment =
  typeof process !== "undefined" && process.env.NODE_ENV === "test";

if (!isTestEnvironment) {
  try {
    configurePdfWorker();
  } catch (error) {
    // Ignored: allows tests or environments without import.meta support to continue.
  }
}

interface PdfViewerProps {
  file: string;
  height?: number | string;
  fileName?: string | null;
  className?: string;
}

interface DocumentLoadSuccess {
  numPages: number;
}

export const PdfViewer: React.FC<PdfViewerProps> = ({
  file,
  height = 400,
  fileName,
  className,
}) => {
  const [numPages, setNumPages] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [containerWidth, setContainerWidth] = useState<number>(0);
  const [zoom, setZoom] = useState<number>(1);
  const viewerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setPageNumber(1);
    setNumPages(0);
    setLoadError(null);
    setIsLoading(true);
    setZoom(1);
  }, [file]);

  useEffect(() => {
    const element = viewerRef.current;
    if (!element) {
      return undefined;
    }

    const updateWidth = () => {
      const rect = element.getBoundingClientRect();
      if (rect.width > 0) {
        setContainerWidth(rect.width);
        return;
      }

      const fallbackWidth = element.clientWidth || element.scrollWidth;
      if (fallbackWidth > 0) {
        setContainerWidth(fallbackWidth);
        return;
      }

      if (typeof window !== "undefined" && window.innerWidth > 0) {
        setContainerWidth(window.innerWidth);
      }
    };

    updateWidth();

    if (typeof ResizeObserver === "undefined") {
      if (typeof window !== "undefined") {
        window.addEventListener("resize", updateWidth);
        return () => {
          window.removeEventListener("resize", updateWidth);
        };
      }

      return undefined;
    }

    const observer = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        setContainerWidth(entry.contentRect.width);
      });
    });

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, []);

  const onDocumentLoadSuccess = useCallback((info: DocumentLoadSuccess) => {
    setNumPages(info.numPages);
    setIsLoading(false);
  }, []);

  const onDocumentLoadError = useCallback((error: Error) => {
    setLoadError(error.message);
    setIsLoading(false);
  }, []);

  const goToPreviousPage = useCallback(() => {
    setPageNumber((current) => {
      if (current <= 1) {
        return current;
      }

      return current - 1;
    });
  }, []);

  const goToNextPage = useCallback(() => {
    setPageNumber((current) => {
      if (numPages === 0 || current >= numPages) {
        return current;
      }

      return current + 1;
    });
  }, [numPages]);

  const canGoToPrevious = useMemo(() => pageNumber > 1, [pageNumber]);
  const canGoToNext = useMemo(
    () => numPages > 0 && pageNumber < numPages,
    [numPages, pageNumber],
  );

  const pageWidth = useMemo(() => {
    if (containerWidth > 0) {
      return containerWidth * zoom;
    }

    if (typeof window !== "undefined" && window.innerWidth > 0) {
      return window.innerWidth * zoom;
    }

    return undefined;
  }, [containerWidth, zoom]);

  const downloadLabel = useMemo(() => fileName ?? "Baixar PDF", [fileName]);

  const increaseZoom = useCallback(() => {
    setZoom((current) => {
      const next = Math.round((current + 0.25) * 100) / 100;
      return next > 2 ? 2 : next;
    });
  }, []);

  const decreaseZoom = useCallback(() => {
    setZoom((current) => {
      const next = Math.round((current - 0.25) * 100) / 100;
      return next < 0.5 ? 0.5 : next;
    });
  }, []);

  const resetZoom = useCallback(() => {
    setZoom(1);
  }, []);

  const canZoomOut = useMemo(() => zoom > 0.5, [zoom]);
  const canZoomIn = useMemo(() => zoom < 2, [zoom]);

  const formattedZoom = useMemo(() => {
    const rounded = Math.round(zoom * 100);
    return `${rounded}%`;
  }, [zoom]);

  return (
    <Paper
      className={className}
      elevation={3}
      sx={{
        display: "flex",
        flexDirection: "column",
        height,
        width: "100%",
        minHeight: 0,
        borderRadius: 3,
        overflow: "hidden",
      }}
    >
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        justifyContent="space-between"
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
          background: (theme) =>
            theme.palette.mode === "light"
              ? "linear-gradient(135deg, #f9fafb 0%, #eef2ff 100%)"
              : theme.palette.background.default,
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Typography variant="subtitle2" color="text.secondary">
            Visualizador de PDF
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {numPages === 0 ? "-" : `${numPages} páginas`}
          </Typography>
        </Stack>
        <Button
          component="a"
          href={file}
          target="_blank"
          rel="noopener noreferrer"
          download={fileName ?? undefined}
          startIcon={<Download />}
          variant="contained"
          size="small"
        >
          {downloadLabel}
        </Button>
      </Stack>
      <Box
        ref={viewerRef}
        sx={{
          position: "relative",
          flex: 1,
          overflow: "auto",
          display: "flex",
          justifyContent: "center",
          alignItems: loadError ? "center" : "flex-start",
          backgroundColor: (theme) =>
            theme.palette.mode === "light"
              ? "#f5f6fb"
              : theme.palette.background.paper,
        }}
      >
        {loadError ? (
          <Stack
            spacing={1}
            alignItems="center"
            justifyContent="center"
            sx={{ p: 3, textAlign: "center" }}
            data-testid="pdf-error"
          >
            <Typography variant="body2">
              Não foi possível carregar o PDF.
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {loadError}
            </Typography>
          </Stack>
        ) : (
          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "center",
              visibility: isLoading ? "hidden" : "visible",
              p: 2,
            }}
          >
            <Document
              file={file}
              onLoadSuccess={onDocumentLoadSuccess}
              onLoadError={onDocumentLoadError}
              loading={null}
            >
              <Page
                pageNumber={pageNumber}
                width={pageWidth}
                renderTextLayer={false}
                renderAnnotationLayer={false}
              />
            </Document>
          </Box>
        )}
        {isLoading && !loadError ? (
          <Stack
            spacing={1}
            alignItems="center"
            justifyContent="center"
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              p: 3,
            }}
            data-testid="pdf-loading"
          >
            <CircularProgress size={24} />
            <Typography variant="body2">Carregando PDF...</Typography>
          </Stack>
        ) : null}
      </Box>
      <Stack
        direction="row"
        spacing={3}
        alignItems="center"
        justifyContent="space-between"
        sx={{
          px: 2,
          py: 1.5,
          borderTop: (theme) => `1px solid ${theme.palette.divider}`,
          flexWrap: "wrap",
          rowGap: 1,
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Tooltip title="Página anterior">
            <span>
              <IconButton
                aria-label="Página anterior"
                onClick={goToPreviousPage}
                disabled={!canGoToPrevious}
                size="small"
                color="primary"
              >
                <ChevronLeft />
              </IconButton>
            </span>
          </Tooltip>
          <Typography variant="body2" data-testid="pdf-page-indicator">
            Página {pageNumber} de {numPages === 0 ? "-" : numPages}
          </Typography>
          <Tooltip title="Próxima página">
            <span>
              <IconButton
                aria-label="Próxima página"
                onClick={goToNextPage}
                disabled={!canGoToNext}
                size="small"
                color="primary"
              >
                <ChevronRight />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          <Tooltip title="Reduzir zoom">
            <span>
              <IconButton
                aria-label="Reduzir zoom"
                onClick={decreaseZoom}
                disabled={!canZoomOut}
                size="small"
              >
                <ZoomOut />
              </IconButton>
            </span>
          </Tooltip>
          <Typography variant="body2" color="text.secondary">
            {formattedZoom}
          </Typography>
          <Tooltip title="Aumentar zoom">
            <span>
              <IconButton
                aria-label="Aumentar zoom"
                onClick={increaseZoom}
                disabled={!canZoomIn}
                size="small"
              >
                <ZoomIn />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Ajustar ao tamanho">
            <IconButton
              aria-label="Ajustar ao tamanho"
              onClick={resetZoom}
              size="small"
            >
              <FitScreen />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>
    </Paper>
  );
};

export default PdfViewer;
